"use client";

import { type JSX, useEffect, useMemo } from "react";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type SearchFilterCatalogProps } from "./search-filter.props";
import { useCatalogFilter } from "./useCatalogFilter";
import { CatalogEditingHint } from "./CatalogEditingHint";
import { FacetSelection } from "lib/search/preselectFacets";
import {
  CATALOG_FACET_STORAGE_KEY,
  PERSISTED_HASH_FACETS,
  PersistedHashFacet,
} from "lib/search/catalogFacetStorage";

const decodeHashComponent = (raw: string): string => {
  try {
    return decodeURIComponent(raw.replaceAll("+", " "));
  } catch {
    return raw;
  }
};

// Parse the raw hash segments (not via URLSearchParams) so the `,` multi-value
// separator stays distinguishable from a literal comma encoded as `%2C` inside
// a single facet value. URLSearchParams would decode both to `,` first, making
// labels containing commas impossible to recover.
const readHashFacetsFromLocation = (): Partial<
  Record<PersistedHashFacet, string[]>
> => {
  const hash = globalThis.location.hash.replace(/^#/, "");
  if (!hash) return {};

  const result: Partial<Record<PersistedHashFacet, string[]>> = {};

  for (const segment of hash.split("&")) {
    const eq = segment.indexOf("=");
    if (eq < 0) continue;

    const key = segment.slice(0, eq);
    const field = PERSISTED_HASH_FACETS.find((f) => key === `f-${f}`);
    if (!field) continue;

    const values = segment
      .slice(eq + 1)
      .split(",")
      .map(decodeHashComponent)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

    if (values.length > 0) {
      result[field] = values;
    }
  }

  return result;
};

export const Default = (props: SearchFilterCatalogProps): JSX.Element => {
  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;
  const datasource = fields?.data?.datasource;
  const searchEngine = useSearchEngine(params?.SearchScope);

  const hashFacets = useMemo<
    Partial<Record<PersistedHashFacet, string[]>>
  >(() => {
    if (typeof globalThis === "undefined" || !globalThis.location) {
      return {};
    }

    return readHashFacetsFromLocation();
  }, []);

  const hasAnchorFacet = useMemo(
    () =>
      (hashFacets.taxonomy_markets?.length ?? 0) > 0 ||
      (hashFacets.taxonomy_industries?.length ?? 0) > 0,
    [hashFacets],
  );

  // Intentionally on-mount only: persists the inbound (e.g. MegaMenu) deep-link
  // facet state once per landing. In-page facet changes after mount are managed
  // by Atomic's UrlManager and are not mirrored here.
  //
  // Markets/industries act as the anchor of a real MegaMenu inbound deep-link:
  // when at least one is in the hash, the full snapshot (including
  // applications/solutions when present) is saved. Otherwise — direct landing
  // or a hash carrying only applications/solutions — the snapshot is cleared
  // so the offering catalog does not inherit stale facets.
  useEffect(() => {
    if (typeof globalThis === "undefined" || !globalThis.localStorage) return;

    try {
      if (hasAnchorFacet) {
        globalThis.localStorage.setItem(
          CATALOG_FACET_STORAGE_KEY,
          JSON.stringify(hashFacets),
        );
      } else {
        globalThis.localStorage.removeItem(CATALOG_FACET_STORAGE_KEY);
      }
    } catch {
      // localStorage unavailable (private mode, quota, blocked) — non-fatal.
    }
  }, [hashFacets, hasAnchorFacet]);

  // Catalog-owned fields: any value the user may have placed in the URL hash
  // for these fields is intentionally overridden by the Sitecore datasource
  // selection. Fields not listed here (industries, markets, ...) are left
  // untouched so MegaMenu-supplied deep links survive.
  const selections: FacetSelection[] = useMemo(() => {
    return hasAnchorFacet && hashFacets?.taxonomy_applications?.length == 1
      ? []
      : [
          {
            field: "taxonomy_applications",
            values: (datasource?.applications?.targetItems || [])
              .map((item) => item.taxonomyLabel?.value ?? "")
              .filter(Boolean),
          },
          {
            field: "taxonomy_solutions",
            values: (datasource?.solutions?.targetItems || [])
              .map((item) => item.taxonomyLabel?.value ?? "")
              .filter(Boolean),
          },
        ];
  }, [datasource, hasAnchorFacet, hashFacets]);

  useCatalogFilter(searchEngine, datasource, datasource?.id, selections);

  if (!isPageEditing) return <></>;

  return (
    <CatalogEditingHint
      heading="Search filter: solution catalog"
      datasource={datasource}
      stylesParam={params?.styles}
    />
  );
};
